import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import axios from "axios";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import CatalogHome from "@/pages/CatalogHome";
import CategoryPage from "@/pages/CategoryPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import QuoteBuilder from "@/pages/QuoteBuilder";
import MyQuotes from "@/pages/MyQuotes";
import AdminDashboard from "@/pages/AdminDashboard";
import AboutPage from "@/pages/AboutPage";
import ManufacturingPage from "@/pages/ManufacturingPage";
import IndustriesPage from "@/pages/IndustriesPage";
import CaseStudiesPage from "@/pages/CaseStudiesPage";
import ShowcaseCategoryPage from "@/pages/ShowcaseCategoryPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Fetch and apply favicon configuration
  useEffect(() => {
    const loadFavicon = async () => {
      try {
        const response = await axios.get(`${API}/config/favicon`);
        const config = response.data;
        
        // Update favicon with cache-busting
        if (config.faviconUrl) {
          // Remove all existing favicon links
          document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']").forEach(el => el.remove());
          
          // Add cache-busting timestamp
          const faviconUrlWithCache = config.faviconUrl + (config.faviconUrl.includes('?') ? '&' : '?') + 't=' + Date.now();
          
          // Create new favicon link (icon)
          const faviconLink = document.createElement('link');
          faviconLink.rel = 'icon';
          faviconLink.type = 'image/png';
          faviconLink.href = faviconUrlWithCache;
          document.head.appendChild(faviconLink);
          
          // Also add shortcut icon for older browsers
          const shortcutIcon = document.createElement('link');
          shortcutIcon.rel = 'shortcut icon';
          shortcutIcon.type = 'image/png';
          shortcutIcon.href = faviconUrlWithCache;
          document.head.appendChild(shortcutIcon);
        }
        
        // Update Apple Touch Icon
        if (config.appleTouchIconUrl) {
          let appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']");
          if (!appleTouchIcon) {
            appleTouchIcon = document.createElement('link');
            appleTouchIcon.rel = 'apple-touch-icon';
            document.head.appendChild(appleTouchIcon);
          }
          appleTouchIcon.href = config.appleTouchIconUrl;
        }
        
        // Update app title meta tag
        if (config.webAppTitle) {
          let appNameMeta = document.querySelector("meta[name='apple-mobile-web-app-title']");
          if (!appNameMeta) {
            appNameMeta = document.createElement('meta');
            appNameMeta.name = 'apple-mobile-web-app-title';
            document.head.appendChild(appNameMeta);
          }
          appNameMeta.content = config.webAppTitle;
        }
      } catch (error) {
        console.error("Error loading favicon config:", error);
      }
    };
    
    loadFavicon();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Auth error:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/manufacturing" element={<ManufacturingPage />} />
            <Route path="/industries" element={<IndustriesPage />} />
            <Route path="/case-studies" element={<CaseStudiesPage />} />
            <Route path="/showcase/:categoryId" element={<ShowcaseCategoryPage />} />
            
            {/* Protected Routes */}
            <Route
              path="/catalog"
              element={user ? <CatalogHome /> : <Navigate to="/login" />}
            />
            <Route
              path="/catalog/:categoryId"
              element={user ? <CategoryPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/product/:productId"
              element={user ? <ProductDetailPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/quote"
              element={user && user.role !== "admin" ? <QuoteBuilder /> : <Navigate to="/login" />}
            />
            <Route
              path="/my-quotes"
              element={user && user.role !== "admin" ? <MyQuotes /> : <Navigate to="/login" />}
            />
            
            {/* Admin Routes */}
            <Route
              path="/admin"
              element={user && user.role === "admin" ? <AdminDashboard /> : <Navigate to="/login" />}
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthContext.Provider>
  );
}

export default App;